import { CircleCheck, Info, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/*
 * One actionable recommendation. Same visual language as the Alerts feed:
 * left-tick severity border + icon, title + body. Tone is decided by the
 * caller (severity + code), the card only renders it.
 */

export type InsightTone = 'bad' | 'warn' | 'info' | 'good';

const TONE_BORDER: Record<InsightTone, string> = {
  bad: 'border-l-bad',
  warn: 'border-l-warn',
  info: 'border-l-brand',
  good: 'border-l-good',
};

const TONE_ICON: Record<InsightTone, string> = {
  bad: 'text-bad',
  warn: 'text-warn',
  info: 'text-brand',
  good: 'text-good',
};

export function InsightCard({
  tone,
  title,
  children,
}: {
  tone: InsightTone;
  title: string;
  children?: ReactNode;
}) {
  const Icon = tone === 'good' ? CircleCheck : tone === 'info' ? Info : TriangleAlert;
  return (
    <div
      className={cn(
        'flex items-start gap-2.5 rounded-md border border-border border-l-4 bg-surface p-3',
        TONE_BORDER[tone],
      )}
    >
      <Icon className={cn('mt-0.5 size-4 shrink-0', TONE_ICON[tone])} aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-label font-semibold text-fg">{title}</p>
        {children ? (
          <p className="mt-0.5 text-label leading-relaxed text-fg-muted">{children}</p>
        ) : null}
      </div>
    </div>
  );
}
